import { type ReactNode, useMemo } from 'react';
import { routes } from 'routes/routes';
import { usePermissions } from 'permissions';
import { isGivingEnabled } from 'common/validators';
import {
  BankFillIcon,
  BankLineIcon,
  BookmarkFillIcon,
  BookmarkLineIcon,
  CalendarFillIcon,
  CalendarLineIcon,
  Classify2FillIcon,
  Classify2LineIcon,
  CompassFillIcon,
  CompassLineIcon,
  HandHeartFillIcon,
  HandHeartLineIcon,
  Home4FillIcon,
  Home4LineIcon,
  MortarboardFillIcon,
  MortarboardLineIcon,
  Notebook2FillIcon,
  Notebook2LineIcon,
  VideoFillIcon,
  VideoLineIcon,
} from 'ui/icons';

export type NavigationMenuConfig = NavigationMenuConfigSections[];

export type NavigationMenuItemConfig = {
  activeIcon: ReactNode;
  alias?: string;
  href: string;
  icon: ReactNode;
  id: string;
  isHidden?: () => boolean;
  key: string;
  text: string;
};

type NavigationMenuConfigSections = {
  collapsedName?: string;
  name?: string;
  section?: NavigationMenuItemConfig[];
};

export const useNavigationMenuConfig = (): NavigationMenuConfig => {
  const { canViewVideos } = usePermissions();

  return useMemo(
    () => [
      {
        section: [
          {
            activeIcon: <Home4FillIcon />,
            href: '/feed',
            icon: <Home4LineIcon />,
            id: 'nav-feed',
            key: '/feed',
            text: 'Home',
          },
          {
            activeIcon: <CalendarFillIcon />,
            alias: routes.events.index,
            href: routes.calendar.index,
            icon: <CalendarLineIcon />,
            id: 'nav-calendar',
            key: routes.calendar.index,
            text: 'Calendar',
          },
          {
            activeIcon: <CompassFillIcon />,
            href: '/channels',
            icon: <CompassLineIcon />,
            id: 'nav-channels',
            key: '/channels',
            text: 'Channels',
          },
          {
            activeIcon: <HandHeartFillIcon />,
            href: routes.giving.now,
            icon: <HandHeartLineIcon />,
            id: 'nav-givings',
            isHidden: () => !isGivingEnabled(),
            key: routes.giving.index,
            text: 'Giving',
          },
        ],
      },
      {
        collapsedName: 'MY',
        name: 'MY STUFF',
        section: [
          {
            activeIcon: <BookmarkFillIcon />,
            href: '/bookmarks',
            icon: <BookmarkLineIcon />,
            id: 'nav-bookmarks',
            key: '/bookmarks',
            text: 'Bookmarks',
          },
          {
            activeIcon: <Classify2FillIcon />,
            href: routes.community.index,
            icon: <Classify2LineIcon />,
            id: 'nav-communities',
            key: routes.community.index,
            text: 'Communities',
          },
          {
            activeIcon: <Notebook2FillIcon />,
            href: '/journal',
            icon: <Notebook2LineIcon />,
            id: 'nav-journal',
            key: '/journal',
            text: 'Journal',
          },
          {
            activeIcon: <BankFillIcon />,
            alias: routes.group.index,
            href: routes.organization.index,
            icon: <BankLineIcon />,
            id: 'nav-organizations',
            key: routes.organization.index,
            text: 'Organizations',
          },
          {
            activeIcon: <MortarboardFillIcon />,
            alias: routes.program.indexAlias,
            href: routes.program.index,
            icon: <MortarboardLineIcon />,
            id: 'nav-programs',
            key: routes.program.index,
            text: 'Programs',
          },
          {
            activeIcon: <VideoFillIcon />,
            href: routes.video.index,
            icon: <VideoLineIcon />,
            id: 'nav-video',
            isHidden: () => !canViewVideos({}),
            key: routes.video.index,
            text: 'Videos',
          },
        ],
      },
    ],
    [canViewVideos],
  );
};
